import React from "react";
import { Check, Filter, Users, X } from "lucide-react";

import { GroupDTO } from "@/generated-client";
import { Localizer } from "@/main/react/Types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from "@/components/ui/drawer";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { getOptimizedSeavienImageUrl, SMALL_THUMBNAIL_IMAGE_SIZE } from "@/main/util/ImageUrlHelper";
import { normalizeDiscussionGroupPublicIds } from "@/main/react/discussion/OpeningThoughtGroupTypes";
import type { LocalizationKey } from "@/main/util/LocalizationUtil";
import { useSelectionInteractionFeedback } from "@/main/react/component/useSelectionInteractionFeedback";
import { useMediaQueryMatches } from "@/main/react/hooks/useMediaQueryMatches";

function localize(localizer: Localizer, key: LocalizationKey, ...args: (string | number)[]): string {
    return localizer(key, ...args);
}

function getAvatarFallback(name?: string | null): string {
    const trimmedName = name?.trim();
    return trimmedName ? trimmedName.charAt(0).toUpperCase() : "G";
}

const GROUP_FILTER_DRAWER_MEDIA_QUERY = "(max-width: 640px)";

/**
 * Below this width the group picker is a bottom drawer rather than a popover.
 *
 * <p>Delegates to `useMediaQueryMatches` (2026-08-15). This was a `useState` mirror re-published from a
 * mount effect — the shape `AGENTS.md` rule 31 forbids, since the breakpoint decides which layout is in
 * the DOM. Same first-render value and same `change` subscription; strictly safer in two places the old
 * body was not, because it guarded only on `typeof window === "undefined"` and would have thrown on a
 * browser without `matchMedia`.
 */
function useIsMobileDrawerBreakpoint(): boolean {
    return useMediaQueryMatches(GROUP_FILTER_DRAWER_MEDIA_QUERY);
}

function GroupSelectionContent(props: {
    groups: GroupDTO[];
    localizer: Localizer;
    selectedGroupPublicIds: string[];
    onSelectedGroupPublicIdsChange: (ids: string[]) => void;
    onClose: () => void;
    isLoading?: boolean;
    onSelectionInteraction: () => void;
}) {
    const {
        groups,
        localizer,
        selectedGroupPublicIds,
        onSelectedGroupPublicIdsChange,
        onClose,
        isLoading = false,
        onSelectionInteraction,
    } = props;

    const selectedSet = React.useMemo(() => new Set(selectedGroupPublicIds), [selectedGroupPublicIds]);
    const myGroupPublicIds = React.useMemo(
        () => normalizeDiscussionGroupPublicIds(groups
            .filter((group) => group.isUserLeaderOfGroup || group.isUserPartOfGroup)
            .map((group) => group.publicId)),
        [groups],
    );

    const toggleGroup = React.useCallback((groupPublicId?: string) => {
        const normalizedGroupPublicId = groupPublicId?.trim();
        if (!normalizedGroupPublicId) {
            return;
        }

        onSelectionInteraction();

        const nextSelected = selectedSet.has(normalizedGroupPublicId)
            ? selectedGroupPublicIds.filter((value) => value !== normalizedGroupPublicId)
            : [...selectedGroupPublicIds, normalizedGroupPublicId];

        onSelectedGroupPublicIdsChange(normalizeDiscussionGroupPublicIds(nextSelected));
    }, [onSelectedGroupPublicIdsChange, onSelectionInteraction, selectedGroupPublicIds, selectedSet]);

    const clearAll = React.useCallback(() => {
        onSelectionInteraction();
        onSelectedGroupPublicIdsChange([]);
    }, [onSelectedGroupPublicIdsChange, onSelectionInteraction]);

    const onlyMyGroups = React.useCallback(() => {
        onSelectionInteraction();
        onSelectedGroupPublicIdsChange(myGroupPublicIds);
    }, [myGroupPublicIds, onSelectedGroupPublicIdsChange, onSelectionInteraction]);

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-2 px-1">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearAll}
                    disabled={selectedGroupPublicIds.length === 0}
                >
                    <X className="size-4" />
                    {localize(localizer, "discussion.thoughts.filter.groups.clear_all")}
                </Button>
                {myGroupPublicIds.length > 0 ? (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onlyMyGroups}
                    >
                        <Users className="size-4" />
                        {localize(localizer, "discussion.thoughts.filter.groups.only_my_groups")}
                    </Button>
                ) : null}
            </div>

            <Command shouldFilter={true}>
                <CommandInput placeholder={localize(localizer, "discussion.thoughts.filter.groups.search_placeholder")} />
                <CommandList>
                    {isLoading && groups.length === 0 ? (
                        <div className="px-4 py-6 text-sm text-muted-foreground">{localizer("home.loading")}</div>
                    ) : null}
                    <CommandEmpty>{localize(localizer, "discussion.thoughts.filter.groups.no_options")}</CommandEmpty>
                    <CommandGroup>
                        <ScrollArea className="max-h-80 pr-2">
                            <div className="flex flex-col gap-1 p-1">
                                {groups.map((group) => {
                                    const groupPublicId = group.publicId?.trim();
                                    if (!groupPublicId) {
                                        return null;
                                    }

                                    const avatarUrl = getOptimizedSeavienImageUrl(group.thumbnailUrl, SMALL_THUMBNAIL_IMAGE_SIZE) || undefined;
                                    const isSelected = selectedSet.has(groupPublicId);
                                    const isViewerGroup = Boolean(group.isUserLeaderOfGroup || group.isUserPartOfGroup);
                                    return (
                                        <CommandItem
                                            key={groupPublicId}
                                            value={`${group.name ?? ""} ${groupPublicId}`}
                                            onSelect={() => toggleGroup(groupPublicId)}
                                            className="rounded-xl px-3 py-3"
                                        >
                                            <div className="flex min-w-0 flex-1 items-center gap-3">
                                                <Avatar size="sm" className="bg-muted">
                                                    <AvatarImage src={avatarUrl} alt={group.name ?? undefined} />
                                                    <AvatarFallback>{getAvatarFallback(group.name)}</AvatarFallback>
                                                </Avatar>
                                                <div className="flex min-w-0 flex-1 flex-col gap-1">
                                                    <div className="truncate font-medium text-foreground">
                                                        {group.name ?? groupPublicId}
                                                    </div>
                                                    <div className="flex flex-wrap gap-1">
                                                        {isViewerGroup ? (
                                                            <Badge variant="secondary">{localize(localizer, "discussion.thoughts.filter.groups.your_group")}</Badge>
                                                        ) : null}
                                                        {group.privateAccess ? (
                                                            <Badge variant="outline">{localize(localizer, "discussion.thoughts.filter.groups.private")}</Badge>
                                                        ) : null}
                                                    </div>
                                                </div>
                                                <div className={cn(
                                                    "flex size-5 items-center justify-center rounded-full border",
                                                    isSelected ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background",
                                                )}>
                                                    {isSelected ? <Check className="size-3.5" /> : null}
                                                </div>
                                            </div>
                                        </CommandItem>
                                    );
                                })}
                            </div>
                        </ScrollArea>
                    </CommandGroup>
                </CommandList>
            </Command>

            <div className="flex justify-end px-1">
                <Button type="button" variant="ghost" size="sm" onClick={onClose}>
                    {localizer("literal.cancel")}
                </Button>
            </div>
        </div>
    );
}

export default function DiscussionThoughtGroupFilter(props: {
    groups: GroupDTO[];
    localizer: Localizer;
    selectedGroupPublicIds: string[];
    onSelectedGroupPublicIdsChange: (ids: string[]) => void;
    hasAvailableGroups?: boolean;
    isLoading?: boolean;
    onOpenRequested?: () => void | Promise<void>;
}) {
    const {
        groups,
        localizer,
        selectedGroupPublicIds,
        onSelectedGroupPublicIdsChange,
        hasAvailableGroups = false,
        isLoading = false,
        onOpenRequested,
    } = props;

    const [open, setOpen] = React.useState(false);
    const isMobile = useIsMobileDrawerBreakpoint();
    const visibleSelectedCount = selectedGroupPublicIds.length > 0 ? selectedGroupPublicIds.length : null;
    const {
        armSelectionInteraction,
        getSelectionSurfaceProps,
        getSelectionCountProps,
    } = useSelectionInteractionFeedback({
        selectionEffect: "filter",
        selectionValue: visibleSelectedCount != null ? selectedGroupPublicIds.join(",") : "all",
        selectionCountFeedback: "pulse",
        visibleCount: visibleSelectedCount,
    });

    const handleOpenChange = React.useCallback((nextOpen: boolean) => {
        setOpen(nextOpen);
        if (nextOpen) {
            void onOpenRequested?.();
        }
    }, [onOpenRequested]);

    /*
     * `DISCUSSION-82` — a filter over ONE option cannot change the outcome, so it does not render.
     *
     * The predicate this widens suppressed only the ZERO-group case, which meant a post attached to a
     * single group still paid a full "Filter by groups · All groups" pill — a 99 CSS px row on a 412 px
     * phone, in the stretch a reader is already scrolling through to reach the conversation, for a
     * control whose only two answers are "the list you are looking at" and "nothing".
     *
     * Three escape hatches, and each one exists because suppressing wrongly is worse than the row:
     *   - `selectedGroupPublicIds.length > 0` — an active selection must always be clearable, even if
     *     the group list arrives shorter than the selection (a group removed under the reader);
     *   - `isLoading` — the control holds its place while the list resolves rather than flickering;
     *   - `groups.length === 0 && hasAvailableGroups` — the LAZY contract. A caller may declare that
     *     groups exist before it has fetched any (the picker fetches on open), and that case must still
     *     offer the control.
     *
     * Counting `groups` is safe here because the page loads the first page of 20 before it says a group
     * exists at all (`PaginationService.groupsForDiscussion`, page size 20): a resolved list of length 1
     * therefore means exactly one group, never "one of many, still paging".
     */
    const hasTooFewGroupsToFilter = groups.length < 2 && !(groups.length === 0 && hasAvailableGroups);
    if (hasTooFewGroupsToFilter && selectedGroupPublicIds.length === 0 && !isLoading) {
        return null;
    }

    const trigger = (
        <Button
            type="button"
            variant="outline"
            className="h-9 min-w-[12.5rem] max-w-full justify-between rounded-full px-3 text-left touch-manipulation [@media(pointer:coarse)]:min-h-11"
            data-playwright-id="discussion-thought-group-filter-trigger"
            disabled={isLoading && groups.length === 0}
            {...getSelectionSurfaceProps({ active: selectedGroupPublicIds.length > 0 })}
        >
            <span className="flex min-w-0 items-center gap-2">
                <Filter className="size-4 shrink-0" />
                <span className="min-w-0 truncate font-medium">
                    {localize(localizer, "discussion.thoughts.filter.groups")}
                </span>
            </span>
            <span className="flex shrink-0 items-center gap-2 text-muted-foreground">
                {selectedGroupPublicIds.length > 0 ? (
                    <Badge variant="secondary" {...getSelectionCountProps()}>{selectedGroupPublicIds.length}</Badge>
                ) : null}
                <span className="text-xs sm:text-sm">
                    {isLoading && groups.length === 0
                        ? localizer("home.loading")
                        : selectedGroupPublicIds.length > 0
                            ? localize(localizer, "discussion.thoughts.filter.groups.selected_count", selectedGroupPublicIds.length)
                            : localize(localizer, "discussion.thoughts.filter.groups.all")}
                </span>
            </span>
        </Button>
    );

    const content = (
        <GroupSelectionContent
            groups={groups}
            localizer={localizer}
            selectedGroupPublicIds={selectedGroupPublicIds}
            onSelectedGroupPublicIdsChange={onSelectedGroupPublicIdsChange}
            onClose={() => setOpen(false)}
            isLoading={isLoading}
            onSelectionInteraction={armSelectionInteraction}
        />
    );

    return (
        <div data-playwright-id="discussion-thought-group-filter">
            {isMobile ? (
                <Drawer open={open} onOpenChange={handleOpenChange}>
                    <DrawerTrigger asChild>{trigger}</DrawerTrigger>
                    <DrawerContent className="max-h-[85vh]">
                        <DrawerHeader>
                            <DrawerTitle>{localize(localizer, "discussion.thoughts.filter.groups")}</DrawerTitle>
                            <DrawerDescription>{localize(localizer, "discussion.thoughts.filter.groups.description")}</DrawerDescription>
                        </DrawerHeader>
                        <div className="px-4 pb-4">{content}</div>
                    </DrawerContent>
                </Drawer>
            ) : (
                <Popover open={open} onOpenChange={handleOpenChange}>
                    <PopoverTrigger asChild>{trigger}</PopoverTrigger>
                    <PopoverContent align="start" className="w-[min(28rem,calc(100vw-2rem))] rounded-2xl p-3">
                        <div className="mb-3 px-1">
                            <div className="font-medium text-foreground">{localize(localizer, "discussion.thoughts.filter.groups")}</div>
                            <div className="text-sm text-muted-foreground">{localize(localizer, "discussion.thoughts.filter.groups.description")}</div>
                        </div>
                        {content}
                    </PopoverContent>
                </Popover>
            )}
        </div>
    );
}
